'use es6';

import I18n from 'I18n';
import Immutable from 'immutable';
import { cleanObject } from '../utils/formatters';
import { ProfileReviewSteps, ReviewSteps } from '../constants/reviews/ReviewSteps';
import { RatingAxes, AppRatingAxes, ThemeRatingAxes, AppQualityRatingsTemplate } from '../constants/reviews/RatingAxes';
import * as ReviewQuestions from '../constants/reviews/ReviewQuestions';
import * as ReviewTypes from '../constants/reviews/ReviewType';
import { PREFER_NOT_TO_SAY } from '../constants/reviews/CompanySize';
import Reply from './Reply';
const REVIEW_TITLE_LIMIT = 100;
const REVIEW_BODY_MIN = 75;
const defaults = {
  id: null,
  reviewType: null,
  offeringId: null,
  // App Marketplace only
  profileId: null,
  // Solutions Marketplace only
  providerPortalId: null,
  // Admin endpoint only
  listingName: null,
  // Admin endpoint only
  reviewerDisplayName: null,
  portalId: null,
  // Customer endpoint only
  userId: null,
  // Customer endpoint only
  avatarUrl: null,
  showAvatar: true,
  industry: null,
  employees: null,
  companyName: null,
  // Solutions Marketplace only TODO deprecate
  offeringVersion: null,
  // Asset Marketplace only
  createdAt: null,
  ratings: new Immutable.Map(),
  answers: new Immutable.Map(),
  reply: new Reply(),
  archivedReplies: new Immutable.List(),
  attributes: new Immutable.List(),
  archived: false
};
export default class Review extends Immutable.Record(defaults) {
  static from(data = {}) {
    let review = new Review(Immutable.fromJS(cleanObject(data)));

    if (data) {
      if (data.reply) {
        review = review.set('reply', Reply.from(data.reply));
      }

      if (data.archivedReplies) {
        review = review.set('archivedReplies', Immutable.List(data.archivedReplies.map(reply => Reply.from(reply))));
      }
    }

    return review;
  }

  static fromArray(data, reviewType) {
    return data.map(review => Review.from(Object.assign({}, review, {
      reviewType
    })));
  }

  static toEmployeeString(range) {
    if (!range) {
      return null;
    }

    let rangeKey;
    let formattedMin;
    let formattedMax;

    if (range === PREFER_NOT_TO_SAY) {
      rangeKey = 'undisclosed';
    } else {
      const [__range, min, max] = range.split('_');
      formattedMin = I18n.formatNumber(min);
      formattedMax = I18n.formatNumber(max);
      rangeKey = min === '1' ? 'single' : max ? 'range' : 'indefinite';
    }

    return I18n.text(`ecosystemMarketplaceCore.reviews.employees.${rangeKey}`, {
      min: formattedMin,
      max: formattedMax
    });
  }

  isStepComplete(reviewType, step, checkedLegalDisclaimer) {
    if (reviewType === ReviewTypes.APP) {
      return this.isAppReviewStepComplete(step, checkedLegalDisclaimer);
    } else if (reviewType === ReviewTypes.THEME) {
      return this.isAssetReviewStepComplete(step, checkedLegalDisclaimer);
    } else {
      return this.isProfileReviewStepComplete(step, checkedLegalDisclaimer);
    }
  }

  isAppReviewStepComplete(step, checkedLegalDisclaimer) {
    switch (step) {
      case ReviewSteps.RATE:
        return AppRatingAxes.every(axis => axis === RatingAxes.SUPPORT_DOCS || !!this.ratings.get(axis));

      case ReviewSteps.SHARE:
        return this.answers.get(ReviewQuestions.TITLE) && this.answers.get(ReviewQuestions.TITLE).length < REVIEW_TITLE_LIMIT && this.answers.get(ReviewQuestions.FOR) && this.answers.get(ReviewQuestions.REVIEW) && this.answers.get(ReviewQuestions.REVIEW).length >= REVIEW_BODY_MIN;

      case ReviewSteps.ABOUT:
        return this.industry && this.employees && checkedLegalDisclaimer;

      default:
        return false;
    }
  }

  isAssetReviewStepComplete(step, checkedLegalDisclaimer) {
    switch (step) {
      case ReviewSteps.RATE:
        return ThemeRatingAxes.every(axis => axis === RatingAxes.SUPPORT_DOCS || !!this.ratings.get(axis));

      case ReviewSteps.SHARE:
        return this.answers.get(ReviewQuestions.TITLE) && this.answers.get(ReviewQuestions.TITLE).length < REVIEW_TITLE_LIMIT && this.answers.get(ReviewQuestions.CUSTOMIZABLE) && this.answers.get(ReviewQuestions.REVIEW) && this.answers.get(ReviewQuestions.REVIEW).length >= REVIEW_BODY_MIN;

      case ReviewSteps.ABOUT:
        return this.industry && this.employees && checkedLegalDisclaimer;

      default:
        return false;
    }
  }

  isProfileReviewStepComplete(step, checkedLegalDisclaimer) {
    switch (step) {
      case ProfileReviewSteps.RATE:
        return this.ratings.get(RatingAxes.OVERALL) && this.answers.get(ReviewQuestions.TITLE) && this.answers.get(ReviewQuestions.TITLE).length < REVIEW_TITLE_LIMIT && this.answers.get(ReviewQuestions.REVIEW) && this.answers.get(ReviewQuestions.REVIEW).length >= REVIEW_BODY_MIN && this.answers.get(ReviewQuestions.SERVICES) && this.answers.get(ReviewQuestions.SERVICES).size;

      case ReviewSteps.SHARE:
        return this.answers.get(ReviewQuestions.TITLE) && this.answers.get(ReviewQuestions.TITLE).length < REVIEW_TITLE_LIMIT && this.answers.get(ReviewQuestions.REVIEW) && this.answers.get(ReviewQuestions.REVIEW).length >= REVIEW_BODY_MIN && this.answers.get(ReviewQuestions.SERVICES) && this.answers.get(ReviewQuestions.SERVICES).size;

      case ProfileReviewSteps.ABOUT:
        return this.industry && this.employees && checkedLegalDisclaimer;

      default:
        return false;
    }
  }

  getHelpfulCount() {
    return this.attributes.filter(attr => attr.get('helpful')).size;
  }

  toggleHelpful(reviewId, userId, helpful) {
    const indexToUpdate = this.attributes.findIndex(attr => attr.get('userId') === userId);

    if (indexToUpdate !== -1) {
      return this.set('attributes', this.attributes.update(indexToUpdate, attr => attr.set('helpful', helpful)));
    }

    return this.set('attributes', this.attributes.push(new Immutable.Map({
      reviewId,
      userId,
      helpful
    })));
  }

  getQualityRatings() {
    const qualityRatingsMap = this.ratings.delete(RatingAxes.OVERALL);

    switch (this.reviewType) {
      case ReviewTypes.APP:
        return AppQualityRatingsTemplate.merge(qualityRatingsMap);

      default:
        return qualityRatingsMap;
    }
  }

  setOverallRating(overallRating) {
    return this.setIn(['ratings', RatingAxes.OVERALL], overallRating);
  }

  setEmployees(employeeSize) {
    return this.set('employees', employeeSize);
  }

  setIndustry(industry) {
    return this.set('industry', industry);
  }

}